import { useMemo } from "react";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import KPIGrid from "@/components/dashboard/KPIGrid";
import TopCustomersChart from "@/components/dashboard/charts/TopCustomersChart";
import useDashboardData from "@/hooks/useDashboardData";
import { Card, CardContent } from "@/components/ui/card";

const formatCount = (value) =>
  new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 0,
  }).format(value || 0);

export default function DashboardCustomers() {
  const forceHistoricalRange = true;
  const {
    overview,
    overviewLoading,
    overviewError,
    comparisonLabel,
    comparisonRangeLabel,
  } = useDashboardData({
    includeForecast: false,
    includeTimeseries: false,
    includeProjects: false,
    includeStatus: false,
    forceHistorical: true,
  });

  const customers = overview?.top_customers || [];

  const kpiItems = useMemo(() => {
    if (!overview) return [];
    const topRevenue = customers[0]?.revenue || 0;
    const avgRevenue = customers.length
      ? customers.reduce((sum, customer) => sum + (customer.revenue || 0), 0) / customers.length
      : 0;
    return [
      {
        key: "top-customer-revenue",
        title: "Top Kunde Umsatz",
        value: topRevenue,
        testId: "kpi-top-customer-revenue",
      },
      {
        key: "avg-customer-revenue",
        title: "Ø Umsatz (Top)",
        value: avgRevenue,
        testId: "kpi-avg-customer-revenue",
      },
      {
        key: "customer-count",
        title: "Aktive Kunden",
        value: customers.length,
        valueFormatter: formatCount,
        testId: "kpi-customer-count",
      },
      {
        key: "revenue",
        title: "Gesamtumsatz",
        value: overview.revenue?.current || 0,
        changePercent: overview.revenue?.change_percent,
        comparisonValue: overview.revenue?.previous,
        comparisonLabel,
        comparisonRangeLabel,
        changeLabel: `ggü. ${comparisonLabel}`,
        testId: "kpi-revenue",
      },
    ];
  }, [customers, overview, comparisonLabel, comparisonRangeLabel]);

  const showHistorical = true;

  return (
    <div className="space-y-6" data-testid="dashboard-customers-view">
      <DashboardFilters
        title="Kunden-Filter"
        subtitle="Erkenne umsatzstarke Kunden im Zeitraum"
        showProjects={false}
        showCustomers
      />


      <section data-testid="dashboard-customers-kpis">
        {!showHistorical && (
          <Card>
            <CardContent className="p-6 text-sm text-slate-600" data-testid="dashboard-no-data">
              Für reine Zukunftszeiträume stehen keine historischen Kundendaten zur Verfügung.
            </CardContent>
          </Card>
        )}
        {overviewLoading && <KPIGrid isLoading />}
        {!overviewLoading && overviewError && (
          <Card>
            <CardContent className="p-6 text-sm text-red-600" data-testid="kpi-error">
              KPI-Daten konnten nicht geladen werden.
            </CardContent>
          </Card>
        )}
        {showHistorical && !overviewLoading && !overviewError && kpiItems.length > 0 && (
          <KPIGrid items={kpiItems} />
        )}
      </section>

      {showHistorical && (
        <section data-testid="dashboard-customers-charts">
          <TopCustomersChart data={customers} isLoading={overviewLoading} error={overviewError} />
        </section>
      )}
    </div>
  );
}